/* No third-party storefront dependencies. Supports Shopify section reloads. */
(() => {
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const bindStack = root => {
    if (root.dataset.bound) return;
    root.dataset.bound = 'true';
    const cards = [...root.querySelectorAll('[data-stack-card]')];
    const buttons = [...root.querySelectorAll('[data-stack-select]')];
    const rail = root.querySelector('.vp-stack-cards');
    const mobile = window.matchMedia('(max-width: 767px)');
    let selected = 0, touchStart = null;
    if (!cards.length) return;
    const show = index => {
      selected = (index + cards.length) % cards.length;
      cards.forEach((card, i) => {
        const offset = (i - selected + cards.length) % cards.length;
        card.style.left = [0, 160, 240, 300][offset] + 'px';
        card.style.transform = ['none', 'rotate(6deg) scale(.86)', 'rotate(10deg) scale(.78)', 'rotate(14deg) scale(.71)'][offset];
        card.style.zIndex = String(40 - offset * 10);
        card.setAttribute('aria-hidden', String(i !== selected));
      });
      buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === selected)));
      rail.style.transform = mobile.matches ? `translateX(-${selected * 100}%)` : '';
      const status = root.querySelector('[data-stack-status]');
      if (status) status.textContent = `${selected + 1} / ${cards.length}`;
    };
    buttons.forEach((button, i) => {
      button.addEventListener('click', () => show(i));
      button.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (selected + 1) % cards.length;
        if (event.key === 'ArrowLeft') next = (selected + cards.length - 1) % cards.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = cards.length - 1;
        if (next !== undefined) { event.preventDefault(); show(next); buttons[next].focus(); }
      });
    });
    cards.forEach((card, i) => card.addEventListener('click', () => show(i)));
    root.querySelector('[data-stack-prev]')?.addEventListener('click', () => show(selected - 1));
    root.querySelector('[data-stack-next]')?.addEventListener('click', () => show(selected + 1));
    rail.addEventListener('touchstart', event => { touchStart = event.touches[0].clientX; }, {passive:true});
    rail.addEventListener('touchend', event => {
      if (touchStart === null || !mobile.matches) return;
      const distance = event.changedTouches[0].clientX - touchStart;
      if (Math.abs(distance) > 45) show(selected + (distance < 0 ? 1 : -1));
      touchStart = null;
    }, {passive:true});
    mobile.addEventListener('change', () => show(selected));
    root.addEventListener('shopify:block:select', event => {
      const card = event.target.closest('[data-stack-card]');
      if (card) show(Number(card.dataset.stackCard));
    });
    show(0);
  };
  const bindProduct = root => {
    if (root.dataset.bound) return;
    root.dataset.bound = 'true';
    const data = JSON.parse(root.querySelector('[data-product-json]').textContent);
    const form = root.querySelector('form');
    const select = form.querySelector('[name=id]');
    const quantity = form.querySelector('[name=quantity]');
    const plan = form.querySelector('[name=selling_plan]');
    const submit = form.querySelector('[type=submit]');
    const status = root.querySelector('[data-product-status]');
    const galleryImage = root.querySelector('[data-gallery-image]');
    const modal = root.querySelector('dialog');
    let chosen = data.variants.find(v => String(v.id) === select.value) || data.variants[0];
    let options = chosen.options.slice();
    const money = cents => new Intl.NumberFormat(data.locale || 'de-DE',{style:'currency',currency:data.currency || 'EUR'}).format(cents / 100);
    const showImage = (src, alt) => {
      if (!galleryImage || !src) return;
      galleryImage.src = src; galleryImage.removeAttribute('srcset'); galleryImage.alt = alt || data.title;
      if (modal) { modal.querySelector('img').src = src; modal.querySelector('img').alt = galleryImage.alt; }
    };
    root.querySelectorAll('[data-gallery-src]').forEach(button => button.addEventListener('click', () => {
      showImage(button.dataset.gallerySrc, button.dataset.galleryAlt);
      root.querySelectorAll('[data-gallery-src]').forEach(other => other.setAttribute('aria-pressed', String(other === button)));
    }));
    root.querySelector('[data-gallery-open]')?.addEventListener('click', () => {
      if (modal && galleryImage) { modal.querySelector('img').src = galleryImage.src; modal.showModal(); }
    });
    modal?.querySelector('button')?.addEventListener('click', () => modal.close());
    modal?.addEventListener('click', event => { if (event.target === modal) modal.close(); });
    const update = () => {
      chosen = data.variants.find(v => v.options.every((value, index) => value === options[index]));
      root.querySelectorAll('[data-option-value]').forEach(button => button.setAttribute('aria-pressed', String(options[Number(button.dataset.optionIndex)] === button.dataset.optionValue)));
      if (!chosen) {
        select.value = ''; submit.disabled = true; status.textContent = 'Diese Kombination ist nicht verfügbar.';
        root.querySelector('[data-total]').textContent = '—'; return;
      }
      select.value = String(chosen.id);
      const rule = chosen.quantity_rule || {min:1,increment:1};
      quantity.min = rule.min || 1; quantity.step = rule.increment || 1;
      if (rule.max) quantity.max = rule.max; else quantity.removeAttribute('max');
      let q = Math.max(Number(quantity.min), Number(quantity.value) || Number(quantity.min));
      q = Number(quantity.min) + Math.ceil((q - Number(quantity.min)) / Number(quantity.step)) * Number(quantity.step);
      if (rule.max) q = Math.min(q, rule.max);
      quantity.value = q;
      root.querySelectorAll('[data-quantity]').forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.quantity) === q)));
      let price = chosen.price;
      if (plan) {
        const allocations = chosen.selling_plan_allocations || [];
        [...plan.options].forEach(o => o.disabled = !allocations.some(a => String(a.selling_plan_id) === o.value));
        if (plan.selectedOptions[0]?.disabled) plan.value = String(allocations[0]?.selling_plan_id || '');
        const subscription = form.querySelector('[name=vp_purchase]:checked')?.value === 'subscription' || data.requires_selling_plan;
        plan.disabled = !subscription;
        plan.closest('[data-plan-wrap]').hidden = !subscription;
        if (subscription) {
          const allocation = allocations.find(a => String(a.selling_plan_id) === plan.value);
          if (!allocation) { submit.disabled = true; status.textContent = 'Für diese Variante ist kein Lieferplan verfügbar.'; root.querySelector('[data-total]').textContent = '—'; return; }
          price = allocation.price;
        }
        root.querySelector('[data-purchase-note]').textContent = subscription ? 'Wiederkehrende Lieferung gemäß gewähltem Lieferplan.' : 'Einmaliger Kauf · Kein Abonnement';
      }
      root.querySelector('[data-total]').textContent = money(price * q);
      root.querySelector('[data-unit-total]').textContent = money(price) + ' pro Stück';
      const unit = root.querySelector('[data-unit-price]');
      if (unit) unit.textContent = chosen.unit_price_text || '';
      submit.disabled = !chosen.available;
      submit.textContent = chosen.available ? 'In den Warenkorb' : 'Ausverkauft';
      status.textContent = chosen.available ? '' : 'Diese Variante ist derzeit ausverkauft.';
      const url = new URL(location.href);url.searchParams.set('variant',chosen.id);history.replaceState({},'',url);
    };
    root.querySelectorAll('[data-option-value]').forEach(button => button.addEventListener('click', () => {
      options[Number(button.dataset.optionIndex)] = button.dataset.optionValue; update();
      if (chosen?.featured_image) showImage(chosen.featured_image.src, chosen.featured_image.alt);
    }));
    select.addEventListener('change', () => { const variant = data.variants.find(v => String(v.id) === select.value); if(variant){options=variant.options.slice();update();} });
    root.querySelectorAll('[data-quantity]').forEach(button => button.addEventListener('click', () => { quantity.value = button.dataset.quantity; update(); }));
    quantity.addEventListener('change', update);
    form.querySelectorAll('[name=vp_purchase]').forEach(input => input.addEventListener('change',update));
    plan?.addEventListener('change',update);
    form.addEventListener('submit', event => {
      update();
      if(submit.disabled || !form.reportValidity()) {event.preventDefault(); return;}
      // Keep native Shopify form submission for cart, errors and checkout compatibility.
    });
    root.classList.add('vp-enhanced');update();
  };
  const init = scope => {
    scope.querySelectorAll('[data-vp-stack]').forEach(bindStack);
    scope.querySelectorAll('[data-vp-product]').forEach(bindProduct);
    scope.querySelectorAll('.vp-menu').forEach(menu => {
      if(menu.dataset.bound)return;menu.dataset.bound='true';
      menu.querySelectorAll('a').forEach(link => link.addEventListener('click',()=>menu.removeAttribute('open')));
      menu.addEventListener('keydown',e=>{if(e.key==='Escape'){menu.removeAttribute('open');menu.querySelector('summary').focus();}});
    });
    scope.querySelectorAll('.vp-review-controls').forEach(controls => {
      if(controls.dataset.bound)return;controls.dataset.bound='true';
      const rail=controls.parentElement.querySelector('.vp-review-rail');
      controls.querySelectorAll('button').forEach(button=>button.addEventListener('click',()=>rail.scrollBy({left:rail.clientWidth*(button.hasAttribute('data-review-next')?1:-1),behavior:reduced()?'instant':'smooth'})));
    });
  };
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(document));else init(document);
  document.addEventListener('shopify:section:load',event=>init(event.target));
})();

/* Reopen the installed Pandectes consent manager; never set consent here. */
document.addEventListener('click', function (event) {
  const trigger = event.target.closest('[data-vp-cookie-settings]');
  if (!trigger) return;
  const status = trigger.parentElement.querySelector('[data-vp-cookie-status]');
  if (window.Pandectes && window.Pandectes.fn && typeof window.Pandectes.fn.openPreferences === 'function') {
    if (status) status.hidden = true;
    window.Pandectes.fn.openPreferences();
  } else if (status) {
    status.textContent = 'Die Cookie-Einstellungen sind derzeit nicht verfügbar. Bitte lade die Seite erneut und versuche es noch einmal.';
    status.hidden = false;
  }
});
